import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedActor,
  updateActor,
} from "../../app/slices/TrainingSlice";
import PiInputfield from "../../components/PiInputField";
import PiSegmentedToggle, {
  PiSegmentedToggleOption,
} from "../../components/PiSegmentedToggle";
import {
  Actor,
  Gender,
  getDefaultRandomInGameNameType,
  InGameNameMode,
  RandomInGameNameType,
} from "../../models/Actor";
import ActorCharacterPresetSelector from "./ActorCharacterPresetSelector";
import ActorOutfitSelector from "./ActorOutfitSelector";
import ActorProfilePictureSelector from "./ActorProfilePictureSelector";
import "./ActorPresetEditor.css";
import ActorPresetRow from "./ActorPresetRow";
import ActorAvatar from "../../components/actors/ActorAvatar";
import { EditorColumnHeader } from "../editor_columns/EditorColumn";
import PiButton from "../../components/PiButton";
import useViewport3D from "../viewport3d/Viewport3D.hook";
import PiCreatorStringDropdown from "../../components/PiCreatorDropdown/PiCreatorStringDropdown";

export default function ActorPresetEditor(props: { isQuickAction?: boolean }) {
  const { isQuickAction } = props;
  const selectedActor = useSelector(selectSelectedActor);
  const isPlayer = selectedActor?.id === "player";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [workingName, setWorkingName] = useState<string>(
    selectedActor?.workingName ?? ""
  );
  const [inGameName, setInGameName] = useState<string>(
    selectedActor?.inGameName ?? selectedActor?.workingName ?? ""
  );
  const [inGameNameMode, setInGameNameMode] = useState<InGameNameMode>(
    selectedActor?.inGameNameMode ?? InGameNameMode.manual
  );
  const [randomInGameNameType, setRandomInGameNameType] =
    useState<RandomInGameNameType>(
      selectedActor?.randomInGameNameType ??
        getDefaultRandomInGameNameType(selectedActor?.gender)
    );
  const { saveQuickActorModal, cancelQuickActorModal } = useViewport3D();

  useEffect(() => {
    setWorkingName(selectedActor?.workingName ?? "");
    setInGameName(selectedActor?.inGameName ?? selectedActor?.workingName ?? "");
    setInGameNameMode(
      selectedActor?.inGameNameMode ?? InGameNameMode.manual
    );
    setRandomInGameNameType(
      selectedActor?.randomInGameNameType ??
        getDefaultRandomInGameNameType(selectedActor?.gender)
    );
  }, [selectedActor]);

  const updateSelectedActor = (updates: Partial<Actor>) => {
    if (!selectedActor) return;

    dispatch(
      updateActor({
        ...selectedActor,
        ...updates,
      })
    );
  };

  const onChangeWorkingName = (newName: string) => {
    if (!selectedActor) return;
    const shouldSyncInGameName =
      (selectedActor.inGameNameMode ?? InGameNameMode.manual) ===
        InGameNameMode.manual &&
      (selectedActor.inGameName ?? selectedActor.workingName) ===
        selectedActor.workingName;

    setWorkingName(newName);
    if (shouldSyncInGameName) {
      setInGameName(newName);
    }

    updateSelectedActor({
      workingName: newName,
      ...(shouldSyncInGameName ? { inGameName: newName } : {}),
    });
  };

  const onChangeInGameName = (newName: string) => {
    setInGameName(newName);
    updateSelectedActor({ inGameName: newName });
  };

  const onChangeInGameNameMode = (newMode: InGameNameMode) => {
    const nextRandomInGameNameType =
      selectedActor?.randomInGameNameType ??
      getDefaultRandomInGameNameType(selectedActor?.gender);
    setInGameNameMode(newMode);
    setRandomInGameNameType(nextRandomInGameNameType);
    updateSelectedActor({
      inGameNameMode: newMode,
      randomInGameNameType: nextRandomInGameNameType,
      ...(newMode === InGameNameMode.manual &&
      !(selectedActor?.inGameName ?? "").trim()
        ? { inGameName: workingName }
        : {}),
    });
  };

  const onChangeRandomInGameNameType = (index: number) => {
    const nextType = RANDOM_IN_GAME_NAME_OPTIONS[index];
    if (!nextType) return;

    setRandomInGameNameType(nextType.value);
    updateSelectedActor({ randomInGameNameType: nextType.value });
  };

  const onChangeBodyType = (newGender: Gender) => {
    updateSelectedActor({ gender: newGender });
  };

  const notForPlayer = (
    <span className="actorpreseteditor-notapplicable">{String(t("actor_preset_editor.not_for_player"))}</span>
  );

  const randomNameOptions = RANDOM_IN_GAME_NAME_OPTIONS.map((option) => ({
    value: option.value,
    label: String(t(option.labelKey)),
  }));
  const selectedRandomInGameNameTypeLabel =
    randomNameOptions.find((option) => option.value === randomInGameNameType)?.label ??
    randomNameOptions[0].label;

  const translateOptions = <T extends string | number>(
    options: Array<PiSegmentedToggleOption<T> & { labelKey: string }>
  ): PiSegmentedToggleOption<T>[] =>
    options.map(({ labelKey, ...option }) => ({ ...option, label: String(t(labelKey)) }));

  // The scene page column header, so it lines up with the list header on the left;
  // the one-line description opens the sheet instead.
  const header =
    isQuickAction || !selectedActor ? null : (
      <EditorColumnHeader
        icon={<ActorAvatar actor={selectedActor} size="24" />}
        title={selectedActor.workingName}
        testId="properties-header"
      />
    );

  const intro =
    isQuickAction || !selectedActor ? null : (
      <p className="actorpreseteditor-intro" data-testid="properties-intro">
        {String(t(isPlayer ? "actor_preset_editor.meta_player" : "actor_preset_editor.meta"))}
      </p>
    );

  const footer = isQuickAction ? (
    <div className="absolute bottom-0 right-0 w-[350px] flex flex-row gap-[10px] h-[40px]">
      <PiButton variant="piRed" onClick={cancelQuickActorModal}>
        Cancel
      </PiButton>
      <PiButton variant="piBlue" onClick={saveQuickActorModal}>
        Save
      </PiButton>
    </div>
  ) : null;

  const inGameNameControl = (
    <div className="actorpreseteditor-stack">
      <div className="actorpreseteditor-line">
        <PiSegmentedToggle
          ariaLabel="in-game-name-mode-toggle"
          className="actorpreseteditor-segment"
          variant="square"
          fullWidth
          layoutId="actorpreseteditor-in-game-name-toggle"
          options={translateOptions(IN_GAME_NAME_MODE_OPTIONS)}
          value={inGameNameMode}
          onChange={onChangeInGameNameMode}
        />
        {inGameNameMode === InGameNameMode.random && (
          <PiCreatorStringDropdown
            selectedItem={selectedRandomInGameNameTypeLabel}
            items={randomNameOptions.map((option) => option.label)}
            onClick={(item) => {
              const index = randomNameOptions.findIndex((option) => option.label === item);
              onChangeRandomInGameNameType(index);
            }}
            dropdownClassName="actorpreseteditor-randomdropdown"
            triggerClassName="actorpreseteditor-randomdropdown-trigger"
            textClassName="actorpreseteditor-randomdropdown-text"
          />
        )}
      </div>
      {inGameNameMode === InGameNameMode.manual && (
        <div className="actorpreseteditor-field">
          <PiInputfield
            label=""
            onChange={onChangeInGameName}
            placeholder={String(t("actor_preset_editor.in_game_name_placeholder"))}
            value={inGameName}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="actorpreseteditor-shell">
      {header}
      <div
        aria-label="actor-preset-window-root"
        className={`actorpreseteditor-scroll pit-creator-scrollbar${
          isQuickAction ? " actorpreseteditor-scroll--quick" : ""
        }`}
      >
        <div className="actorpreseteditor-sheet" data-testid="actor-preset-sheet">
          {intro}
          <ActorPresetRow
            name={String(t("actor_preset_editor.actor_id"))}
            hint={isPlayer ? undefined : String(t("actor_preset_editor.actor_id_hint"))}
            testId="actor-preset-row-actor-id"
          >
            {isPlayer ? (
              notForPlayer
            ) : (
              <div className="actorpreseteditor-field">
                <PiInputfield
                  label=""
                  onChange={onChangeWorkingName}
                  placeholder={String(t("actor_preset_editor.actor_id_placeholder"))}
                  value={workingName}
                />
              </div>
            )}
          </ActorPresetRow>
          <ActorPresetRow
            name={String(t("actor_preset_editor.avatar_colour"))}
            hint={isPlayer ? undefined : String(t("actor_preset_editor.avatar_colour_hint"))}
            testId="actor-preset-row-avatar-colour"
          >
            {isPlayer ? notForPlayer : <ActorProfilePictureSelector />}
          </ActorPresetRow>
          <div className="actorpreseteditor-rule" role="separator" />
          <ActorPresetRow
            name={String(t("actor_preset_editor.in_game_name"))}
            testId="actor-preset-row-in-game-name"
          >
            {isPlayer ? notForPlayer : inGameNameControl}
          </ActorPresetRow>
          <ActorPresetRow
            name={String(t("actor_preset_editor.body_type"))}
            testId="actor-preset-row-body-type"
          >
            {isPlayer ? (
              notForPlayer
            ) : (
              <PiSegmentedToggle
                ariaLabel="body-type-toggle"
                className="actorpreseteditor-segment actorpreseteditor-control"
                variant="square"
                fullWidth
                layoutId="actorpreseteditor-body-type-toggle"
                options={translateOptions(BODY_TYPE_OPTIONS)}
                value={selectedActor?.gender ?? Gender.random}
                onChange={onChangeBodyType}
              />
            )}
          </ActorPresetRow>
          <ActorPresetRow
            name={String(t("actor_preset_editor.face"))}
            testId="actor-preset-row-face"
          >
            {isPlayer ? notForPlayer : <ActorCharacterPresetSelector />}
          </ActorPresetRow>
          <ActorPresetRow
            name={String(t("actor_preset_editor.outfit"))}
            testId="actor-preset-row-outfit"
          >
            <ActorOutfitSelector />
          </ActorPresetRow>
        </div>
      </div>
      {footer}
    </div>
  );
}

const RANDOM_IN_GAME_NAME_OPTIONS: Array<{
  labelKey: string;
  value: RandomInGameNameType;
}> = [
  { labelKey: "actor_preset_editor.name_neutral", value: RandomInGameNameType.neutral },
  { labelKey: "actor_preset_editor.name_male", value: RandomInGameNameType.male },
  { labelKey: "actor_preset_editor.name_female", value: RandomInGameNameType.female },
];

const IN_GAME_NAME_MODE_OPTIONS: Array<PiSegmentedToggleOption<InGameNameMode> & { labelKey: string }> = [
  {
    testId: "in-game-name-mode-manual",
    label: "Manual",
    labelKey: "actor_preset_editor.manual",
    value: InGameNameMode.manual,
  },
  {
    testId: "in-game-name-mode-random",
    label: "Random",
    labelKey: "actor_preset_editor.random",
    value: InGameNameMode.random,
  },
];

const BODY_TYPE_OPTIONS: Array<PiSegmentedToggleOption<Gender> & { labelKey: string }> = [
  { testId: "body-type-male", label: "Male", labelKey: "actor_preset_editor.male", value: Gender.male },
  { testId: "body-type-female", label: "Female", labelKey: "actor_preset_editor.female", value: Gender.female },
  { testId: "body-type-random", label: "Random", labelKey: "actor_preset_editor.random", value: Gender.random },
];
